import { h, Host } from "@stencil/core";
import { adoptGlobalStyleSheet } from "../style/ch-global-stylesheet";
const CONTAINING_BLOCK_RESET_CUSTOM_VAR = "--ch-window-relative-position";
/**
 * The 'ch-window' component represents a popup container that is positioned
 * relative to an element or the screen.
 */
export class ChWindow {
    constructor() {
        this.container = undefined;
        this.xAlign = "center";
        this.yAlign = "center";
        this.hidden = true;
        this.modal = true;
        this.caption = "";
        this.closeText = undefined;
        this.closeTooltip = undefined;
        this.closeOnOutsideClick = undefined;
        this.closeOnEscape = undefined;
        this.allowDrag = "no";
        this.showFooter = true;
        this.showHeader = true;
        this.showMain = true;
        this.showSeparation = false;
    }
    isContainerCssOverride = false;
    relativeWindow = false;
    containerResizeObserver;
    mask;
    header;
    window;
    validCssAligns = [
        "outside-start",
        "inside-start",
        "center",
        "inside-end",
        "outside-end"
    ];
    dragStartX;
    dragStartY;
    draggedX = 0;
    draggedY = 0;
    el;
    containerHandler(value, oldValue) {
        this.checkRelativePosition();
        this.containerResizeObserverHandler(value, oldValue);
        this.updatePosition();
    }
    hiddenHandler() {
        if (this.hidden) {
            this.resetDrag();
            this.removeListeners();
            this.windowClosed.emit();
        }
        else {
            this.checkRelativePosition();
            this.updatePosition();
            this.watchCSSAlign();
            this.addListeners();
            this.windowOpened.emit();
        }
    }
    /** Emitted when the window is opened. */
    windowOpened;
    /** Emitted when the window is closed. */
    windowClosed;
    componentWillLoad() {
        this.containerResizeObserver = new ResizeObserver(this.updatePosition);
        this.containerResizeObserverHandler(this.container);
        this.watchCSSAlign();
        this.loadGlobalStyleSheet();
    }
    componentDidLoad() {
        if (!this.hidden) {
            this.addListeners();
        }
    }
    mousedownHandler(eventInfo) {
        if (this.isDraggable(eventInfo.composedPath())) {
            this.dragStartX = eventInfo.clientX;
            this.dragStartY = eventInfo.clientY;
            document.addEventListener("mousemove", this.mousemoveHandler, {
                passive: true
            });
            document.addEventListener("mouseup", this.mouseupHandler, {
                once: true
            });
        }
    }
    windowCloseClickedHandler() {
        this.hidden = true;
    }
    mousemoveHandler = (eventInfo) => {
        const translateX = this.draggedX + eventInfo.clientX - this.dragStartX;
        const translateY = this.draggedY + eventInfo.clientY - this.dragStartY;
        this.window.style.setProperty("--ch-window-x-drag", `${translateX}px`);
        this.window.style.setProperty("--ch-window-y-drag", `${translateY}px`);
    };
    mouseupHandler = () => {
        document.removeEventListener("mousemove", this.mousemoveHandler);
        this.draggedX = parseInt(this.window.style.getPropertyValue("--ch-window-x-drag"));
        this.draggedY = parseInt(this.window.style.getPropertyValue("--ch-window-y-drag"));
    };
    maskClickHandler = (eventInfo) => {
        eventInfo.stopPropagation();
    };
    checkRelativePosition() {
        const computed = getComputedStyle(this.el);
        this.relativeWindow = !!computed.getPropertyValue(CONTAINING_BLOCK_RESET_CUSTOM_VAR);
    }
    updatePosition = () => {
        if (!this.isContainerCssOverride && this.container && this.mask) {
            const rect = this.container.getBoundingClientRect();
            this.el.style.setProperty("--ch-window-mask-width", `${rect.width}px`);
            this.el.style.setProperty("--ch-window-mask-height", `${rect.height}px`);
            // Nested windows are positioned relative to its initial containing block,
            // so there is no need to align them relative to the document
            if (this.relativeWindow) {
                return;
            }
            this.el.style.setProperty("--ch-window-inset-inline-start", `${rect.left}px`);
            this.el.style.setProperty("--ch-window-inset-block-start", `${rect.top}px`);
        }
        else if (this.isContainerCssOverride || !this.container) {
            this.el.style.removeProperty("--ch-window-mask-width");
            this.el.style.removeProperty("--ch-window-mask-height");
            if (this.relativeWindow) {
                return;
            }
            this.el.style.removeProperty("--ch-window-inset-inline-start");
            this.el.style.removeProperty("--ch-window-inset-block-start");
        }
    };
    resetDrag() {
        this.dragStartX = undefined;
        this.dragStartY = undefined;
        this.draggedX = 0;
        this.draggedY = 0;
        this.window.style.removeProperty("--ch-window-x-drag");
        this.window.style.removeProperty("--ch-window-y-drag");
    }
    isDraggable(target) {
        return (this.allowDrag !== "no" &&
            ((this.allowDrag === "header" && target.includes(this.header)) ||
                (this.allowDrag === "box" && target.includes(this.window))));
    }
    watchCSSAlign() {
        const style = getComputedStyle(this.el);
        const container = style.getPropertyValue("--ch-window-container").trim();
        const xAlign = style
            .getPropertyValue("--ch-window-align-x")
            .trim();
        const yAlign = style
            .getPropertyValue("--ch-window-align-y")
            .trim();
        this.isContainerCssOverride = container.includes("window");
        if (this.validCssAligns.includes(xAlign)) {
            this.xAlign = xAlign;
        }
        if (this.validCssAligns.includes(yAlign)) {
            this.yAlign = yAlign;
        }
    }
    containerResizeObserverHandler(observe, unobserve) {
        if (observe) {
            this.containerResizeObserver.observe(observe);
        }
        if (unobserve) {
            this.containerResizeObserver.unobserve(unobserve);
        }
    }
    addListeners() {
        window.addEventListener("resize", this.windowResizeHandler, {
            passive: true
        });
        if (this.container) {
            document.addEventListener("scroll", this.windowScrollHandler, {
                capture: true,
                passive: true
            });
        }
        if (this.closeOnOutsideClick) {
            document.addEventListener("click", this.closeOnOutsideClickHandler, {
                capture: true
            });
        }
        if (this.closeOnEscape) {
            document.addEventListener("keydown", this.closeOnEscapeHandler, {
                capture: true
            });
        }
    }
    removeListeners() {
        window.removeEventListener("resize", this.windowResizeHandler);
        document.removeEventListener("scroll", this.windowScrollHandler, {
            capture: true
        });
        document.removeEventListener("click", this.closeOnOutsideClickHandler, {
            capture: true
        });
        document.removeEventListener("keydown", this.closeOnEscapeHandler, {
            capture: true
        });
    }
    windowResizeHandler = () => {
        this.updatePosition();
        this.watchCSSAlign();
    };
    windowScrollHandler = () => {
        this.updatePosition();
    };
    closeOnOutsideClickHandler = (eventInfo) => {
        if (!eventInfo.composedPath().includes(this.window)) {
            this.hidden = true;
        }
    };
    closeOnEscapeHandler = (eventInfo) => {
        if (eventInfo.key === "Escape") {
            this.hidden = true;
        }
    };
    loadGlobalStyleSheet() {
        adoptGlobalStyleSheet(this.el.shadowRoot.adoptedStyleSheets);
    }
    render() {
        const separationY = this.yAlign === "outside-start" || this.yAlign === "outside-end";
        const separationX = this.xAlign === "outside-start" || this.xAlign === "outside-end";
        return (h(Host, { style: this.relativeWindow && {
                "--ch-window-inset-inline-start": "0px",
                "--ch-window-inset-block-start": "0px"
            } }, h("div", { class: "mask", part: "mask", ref: el => (this.mask = el), onClick: this.maskClickHandler }, h("section", { class: "window", part: "window", ref: el => (this.window = el) }, this.showHeader && (h("header", { part: "header", ref: el => (this.header = el) }, h("slot", { name: "header" }, h("span", { part: "caption" }, this.caption), h("ch-window-close", { part: "close", title: this.closeTooltip }, this.closeText)))), this.showMain ? (h("div", { part: "main" }, h("slot", null))) : (h("slot", null)), this.showFooter && (h("footer", { part: "footer" }, h("slot", { name: "footer" })))), this.showSeparation && (h("div", { class: {
                separation: true,
                "separation--x": separationX && !separationY,
                "separation--y": separationY && !separationX,
                "separation--both": separationX && separationY
            }, part: "separation" })))));
    }
    static get is() { return "ch-window"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["ch-window.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ch-window.css"]
        };
    }
    static get properties() {
        return {
            "container": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "HTMLElement",
                    "resolved": "HTMLElement",
                    "references": {
                        "HTMLElement": {
                            "location": "global",
                            "id": "global::HTMLElement"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The container element for the window."
                }
            },
            "xAlign": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "ChWindowAlign",
                    "resolved": "\"center\" | \"inside-end\" | \"inside-start\" | \"outside-end\" | \"outside-start\"",
                    "references": {
                        "ChWindowAlign": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/window/ch-window.tsx",
                            "id": "src/components/window/ch-window.tsx::ChWindowAlign"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The horizontal alignment of the window."
                },
                "attribute": "x-align",
                "reflect": true,
                "defaultValue": "\"center\""
            },
            "yAlign": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "ChWindowAlign",
                    "resolved": "\"center\" | \"inside-end\" | \"inside-start\" | \"outside-end\" | \"outside-start\"",
                    "references": {
                        "ChWindowAlign": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/window/ch-window.tsx",
                            "id": "src/components/window/ch-window.tsx::ChWindowAlign"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The vertical alignment of the window."
                },
                "attribute": "y-align",
                "reflect": true,
                "defaultValue": "\"center\""
            },
            "hidden": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determines if the window is hidden or visible."
                },
                "attribute": "hidden",
                "reflect": true,
                "defaultValue": "true"
            },
            "modal": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies whether the window should be displayed as a modal."
                },
                "attribute": "modal",
                "reflect": true,
                "defaultValue": "true"
            },
            "caption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The caption or title of the window."
                },
                "attribute": "caption",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "closeText": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The text for the close button."
                },
                "attribute": "close-text",
                "reflect": false
            },
            "closeTooltip": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The tooltip text for the close button."
                },
                "attribute": "close-tooltip",
                "reflect": false
            },
            "closeOnOutsideClick": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determines whether the window should close when clicked outside."
                },
                "attribute": "close-on-outside-click",
                "reflect": false
            },
            "closeOnEscape": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determines whether the window should close when the Escape key is pressed."
                },
                "attribute": "close-on-escape",
                "reflect": false
            },
            "allowDrag": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"no\" | \"header\" | \"box\"",
                    "resolved": "\"box\" | \"header\" | \"no\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the drag behavior of the window."
                },
                "attribute": "allow-drag",
                "reflect": false,
                "defaultValue": "\"no\""
            },
            "showFooter": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if a footer is rendered at the bottom of the\nwindow."
                },
                "attribute": "show-footer",
                "reflect": false,
                "defaultValue": "true"
            },
            "showHeader": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if a header is rendered on top of the window."
                },
                "attribute": "show-header",
                "reflect": false,
                "defaultValue": "true"
            },
            "showMain": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if a div wrapper is rendered for the\ndefault slot."
                },
                "attribute": "show-main",
                "reflect": false,
                "defaultValue": "true"
            },
            "showSeparation": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if a div between the container and the window\nspace."
                },
                "attribute": "show-separation",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get events() {
        return [{
                "method": "windowOpened",
                "name": "windowOpened",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the window is opened."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "windowClosed",
                "name": "windowClosed",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the window is closed."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "container",
                "methodName": "containerHandler"
            }, {
                "propName": "hidden",
                "methodName": "hiddenHandler"
            }];
    }
    static get listeners() {
        return [{
                "name": "mousedown",
                "method": "mousedownHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "windowCloseClicked",
                "method": "windowCloseClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=ch-window.js.map
